import { useEffect, useRef, useState } from "react";
import { Modal } from ".";
import { Order } from "../../Transactions/Order";
import Badge from "../badge/Badge";
import Button from "../button/Button";
import { format } from "date-fns";
import Alert from "../alert/Alert";
import { cache, request } from "../../../utils";
import { InfoIcon } from "../../../icons";
import axios from "axios";
import jsPDF from "jspdf";
import { ChevronDown, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import {
  BLOCKCHAIN_NETWORK,
  EMAIL_PAYMENT_METHODS,
  MOMO_COUNTRIES_ISO2,
  ORDER_STATUS,
  PAYMENT_METHOD,
} from "../../../utils/constant";
import CopyButton from "../button/copyButton";
import {
  getBlockchainName,
  getOrderStatusBadgeColor,
} from "../../../utils/helpers";
import { useOrdersStore } from "../../../stores/useOrderStore";

interface User {
  _id: string;
  token: string;
}

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
}

interface DetailRow {
  label: string;
  value: string;
}

export default function OrderDetailsModal({
  isOpen,
  onClose,
  order,
}: OrderDetailsModalProps) {
  const [isSellerModalOpen, setSellerModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [buyerMadePayment, setBuyerMadePayment] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const { updateOrder } = useOrdersStore();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        downloadMenuRef.current &&
        !downloadMenuRef.current.contains(e.target as Node)
      ) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!order) return null;

  const orderId = order._id;
  // const buyerCreditPaymentMethod = order.buyerCreditPaymentMethod;
  const buyerDebitPaymentMethod = order.buyerDebitPaymentMethod;

  const paymentClose = () => {
    onClose();
    setPaymentProof(null);
    setSellerModalOpen(false);
    setError(null);
    setSuccess(null);
    setBuyerMadePayment(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
        "image/webp",
        "image/heic",
      ];

      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        setTimeout(() => setError(""), 4000);
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        setError(
          "Invalid file type. Only JPG, PNG, WEBP, HEIC, and PDF are allowed.",
        );
        setTimeout(() => setError(""), 4000);
        return;
      }

      setError(""); // Clear any existing error
      setPaymentProof(file);
    }
  };

  const handleProofSubmit = async () => {
    const user = cache.get("USER") as User | null;

    if (!user) {
      setError("Session expired. Please sign in again.");
      return;
    }

    const userId = user._id;
    const token = user.token;

    // Validate that a payment proof is uploaded if payment method is MOBILE_MONEY
    if (
      order.sellerCreditPaymentDetails?.paymentMethod === "MOBILE_MONEY" &&
      !paymentProof
    ) {
      setError("Please upload proof of payment");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // ---- Initialize variable to store payment proof URL
      let buyerPaymentProofUrl: string | undefined;

      // ---- Only proceed with the file upload if payment method is MOBILE_MONEY
      if (
        order.sellerCreditPaymentDetails?.paymentMethod === "MOBILE_MONEY" &&
        paymentProof
      ) {
        const filename = paymentProof.name;

        // ---- Step 1: Get presigned URL from backend
        const presignedRes = await axios.get<{
          data: { uploadLink: string; Key: string };
        }>(
          `${
            import.meta.env.VITE_USER_GATEWAY_BASE_URL
          }/file-manager/file-upload-link`,
          {
            params: {
              filename,
              purpose: "PAYMENT_PROOF",
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // ---- Ensure presigned URL and Key are returned
        if (
          !presignedRes?.data?.data?.uploadLink ||
          !presignedRes?.data?.data?.Key
        ) {
          throw new Error("Failed to generate presigned URL");
        }

        const { uploadLink, Key } = presignedRes.data.data;

        // ---- Step 2: Upload file to S3 using presigned URL
        const uploadRes = await axios.put(uploadLink, paymentProof);

        if (!uploadRes) {
          throw new Error("Failed to upload payment proof to S3");
        }

        // ---- Step 3: Set the Key as the proof URL to backend
        buyerPaymentProofUrl = encodeURI(
          Key.startsWith("/") ? Key.slice(1) : Key,
        );
      }

      // ---- Step 4: Notify backend of payment confirmation (if file exists)
      const dataPayload = buyerPaymentProofUrl ? { buyerPaymentProofUrl } : {};

      const idempotencyKey = `BUYERPAID::${userId}-${order._id}-${order.fromCurrency.abbr}-${order.amount}`;

      await request.post<any>({
        url: `/order/${orderId}/buyer-paid`,
        data: dataPayload,
        headers: {
          Authorization: `Bearer ${token}`,
          idempotencykey: idempotencyKey,
          "Content-Type": "application/json",
        },
      });

      updateOrder(order._id, {
        status: ORDER_STATUS.PROCESSING,
        buyerMadePayment: true,
      });

      setBuyerMadePayment(true);

      if (order.sellerCreditPaymentDetails?.paymentMethod === "MOBILE_MONEY") {
        setSuccess("Payment confirmation submitted successfully.");
      } else {
        setSuccess("Payment sent.");
      }
    } catch (err) {
      console.error("Error confirming payment:", err);
      setError((err as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMMM do yyyy, h:mm a");
  };

  const removeUnderscoreAndCapitalize = (str: string = "") => {
    if (!str) return "";
    return str
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // ---- Formatt rate
  const formatRateDisplay = () => {
    const from = order?.fromCurrency;
    const to = order?.toCurrency;

    if (!from || !to || from.displayRate == null || to.displayRate == null)
      return "";

    if (from.displayRate === 1) {
      return `1 ${from.abbr} = ${to.abbr}${to.displayRate.toFixed(2)}`;
    } else if (to.displayRate === 1) {
      return `1 ${to.abbr} = ${from.abbr}${from.displayRate.toFixed(2)}`;
    }

    // Fallback if neither is exactly 1
    return `1 ${from.abbr} = ${to.abbr}${to.displayRate.toFixed(2)}`;
  };

  const renderblockchainName = () => {
    const network = order?.sellerCreditPaymentDetails
      ?.network as unknown as BLOCKCHAIN_NETWORK;
    return getBlockchainName(network);
  };

  const showValue = (order: Order) => {
    if (order.fee > 0) {
      const value = order.value - order.fee;
      return `${order.toCurrency.abbr} ${value.toLocaleString()}`;
    } else {
      return `${order.toCurrency.abbr} ${order.value.toLocaleString()}`;
    }
  };

  const loadImage = (url: string): Promise<HTMLImageElement | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const loadImageAsDataURL = (
  url: string,
): Promise<{ dataUrl: string; width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL("image/png"),
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = reject;
    img.src = url;
  });
};

  const circularFlagCanvas = (
    img: HTMLImageElement,
    size = 120,
  ): HTMLCanvasElement => {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d")!;
    const ringWidth = size * 0.06;

    // white ring
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();

    // clipped flag image
    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - ringWidth, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(
      img,
      size / 2 - (size / 2 - ringWidth),
      size / 2 - (size / 2 - ringWidth),
      size - ringWidth * 2,
      size - ringWidth * 2,
    );
    ctx.restore();

    return canvas;
  };


  const loadFlagCanvases = async () => {
    const [fromImg, toImg] = await Promise.all([
      loadImage(order.fromCurrency.flag),
      loadImage(order.toCurrency.flag),
    ]);
    return {
      from: fromImg ? circularFlagCanvas(fromImg) : null,
      to: toImg ? circularFlagCanvas(toImg) : null,
    };
  };

  const getMainRows = (): DetailRow[] => {
    const rows: DetailRow[] = [
      {
        label: "Currency Pair",
        value: `${order.fromCurrency.abbr}/${order.toCurrency.abbr}`,
      },
      {
        label: "Amount to pay",
        value: `${order.fromCurrency.abbr}${order.amount.toLocaleString()}`,
      },
    ];

    if (order.fee > 0) {
      rows.push({
        label: "Amount",
        value: `${order.toCurrency.abbr}${order.value.toLocaleString()}`,
      });
      rows.push({
        label: "Fee",
        value: `${order.toCurrency.abbr}${order.fee.toLocaleString()}`,
      });
    }

    rows.push({ label: "Amount to receive", value: showValue(order) });
    rows.push({ label: "Rate", value: formatRateDisplay() });
    rows.push({ label: "Date", value: formatDate(order.createdAt) });
    rows.push({
      label: "Reference",
      value: String(order.paymentReferenceCode ?? "N/A"),
    });

    if (order.narration) {
      rows.push({ label: "Narration", value: order.narration });
    }

    rows.push({ label: "Status", value: order.status });
    rows.push({
      label: "Payment Made",
      value: order.buyerMadePayment ? "Yes" : "No",
    });

    if (order.sellerCreditPaymentDetails?.paymentMethod === "MOBILE_MONEY") {
      rows.push({
        label: "Payment Proof",
        value: order.hasBuyerPaymentProof ? "Uploaded" : "Not uploaded",
      });
    }

    if (order.source) {
      rows.push({
        label: "Source",
        value: removeUnderscoreAndCapitalize(order.source),
      });
    }

    return rows;
  };
  const getPaymentDetailRows = (
    details: typeof order.sellerCreditPaymentDetails | typeof order.buyerCreditPaymentDetails | typeof order.buyerDebitPaymentDetails,
  ): DetailRow[] => {
    if (!details) return [];
    const rows: DetailRow[] = [];

    switch (details.paymentMethod) {
  case PAYMENT_METHOD.BANK_TRANSFER:
  case PAYMENT_METHOD.ACH:
  case PAYMENT_METHOD.WIRE:
        rows.push({ label: "Account Name", value: details.accountName || "" });
        rows.push({
          label: "Account Number",
          value: details.accountNumber || "",
        });
        if (details.currency?.bankCodeLabel) {
          rows.push({
            label: details.currency.bankCodeLabel,
            value: details.bankCode || "",
          });
        }
        if (details.providerName) {
          rows.push({ label: "Bank Name", value: details.providerName });
        }
        if (
          details.holderCity ||
          details.holderStreet ||
          details.holderPostalCode
        ) {
          rows.push({
            label: "Holder's Address",
            value: [
              details.holderStreet,
              details.holderCity,
              details.holderPostalCode,
            ]
              .filter(Boolean)
              .join(", "),
          });
        }
        break;

      case PAYMENT_METHOD.WECHAT_PAY:
      case PAYMENT_METHOD.ALIPAY:
        rows.push({ label: "Account Name", value: details.accountName || "" });
        rows.push({ label: "Phone Number", value: details.phoneNumber || "" });
        break;

      case PAYMENT_METHOD.MOBILE_MONEY:
        rows.push({
          label: details.isUssdCode ? "Account Name" : "MoMo Name",
          value: details.accountName || "",
        });
        rows.push({
          label: details.isUssdCode ? "USSD Code" : "MoMo Number",
          value: details.isUssdCode
            ? `${details.accountNumber}${order.amount}#`
            : details.accountNumber || "",
        });
        if (details.providerName) {
          rows.push({ label: "Network Carrier", value: details.providerName });
        }
        // network is an object on buyerCreditPaymentDetails ({name, logo, code, hasGateway})
        if (details.network && typeof details.network === "object" && "name" in details.network) {
          rows.push({
            label: "Network",
            value: (details.network as { name: string }).name,
          });
        }
        break;

      case PAYMENT_METHOD.STABLE_CURRENCY:
        rows.push({
          label: "Wallet Address",
          value: details.walletAddress || "",
        });
        rows.push({
          label: "Network",
          value: getBlockchainName(
            details.network as unknown as BLOCKCHAIN_NETWORK,
          ),
        });
        break;

      default:
        if (
          details.paymentMethod &&
          EMAIL_PAYMENT_METHODS.has(details.paymentMethod)
        ) {
          rows.push({ label: "Account Name", value: details.accountName || "" });
          rows.push({ label: "Email", value: details.email || "" });
          rows.push({
            label: "Payment Method",
            value: details.paymentMethod,
          });
        }
        break;
    }

    return rows;
  };

  // ---- Shared status color lookup (used by both PDF and PNG output)
  const getStatusColors = (): {
    rgb: [number, number, number];
    rgbBg: [number, number, number];
  } => {
    const map: Record<string, [[number, number, number], [number, number, number]]> = {
      SUCCESSFUL: [[22, 163, 74], [220, 252, 231]],
      PENDING: [[202, 138, 4], [254, 249, 195]],
      PROCESSING: [[202, 138, 4], [254, 249, 195]],
      FAILED: [[220, 38, 38], [254, 226, 226]],
    };
    const [rgb, rgbBg] = map[order.status] || [[75, 85, 99], [243, 244, 246]];
    return { rgb, rgbBg };
  };

  const receiptFilename = (ext: string) =>
    `order-${order.paymentReferenceCode || order._id}.${ext}`;


  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // const flags = await loadFlagCanvases();

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 44;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      try {
        const logo = await loadImageAsDataURL("/images/logo/SecondaryLogoPNG.png");
        const logoMaxWidth = 120;
        const logoMaxHeight = 56;
        const ratio = Math.min(logoMaxWidth / logo.width, logoMaxHeight / logo.height);
        const logoW = logo.width * ratio;
        const logoH = logo.height * ratio;
        doc.addImage(
          logo.dataUrl,
          "PNG",
          pageWidth - margin - logoW,
          margin - 8,
          logoW,
          logoH
        );
      } catch (e) {
        console.warn("Could not load logo for PDF", e);
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(17, 24, 39);
      doc.text("Order Receipt", margin, y);
      y += 22;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      doc.text(
        `${order.fromCurrency.abbr}/${order.toCurrency.abbr} | ${formatDate(order.createdAt)}`,
        margin,
        y
      );
      y += 32;

      // ---- Big amount + status badge
      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      doc.setTextColor(17, 24, 39);
      doc.text(`${order.fromCurrency.abbr}${order.amount.toLocaleString()}`, margin, y);

      const { rgb, rgbBg } = getStatusColors();
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      const badgeText = order.status;
      const badgeTextWidth = doc.getTextWidth(badgeText);
      const badgePaddingX = 9;
      const badgeHeight = 18;
      const badgeWidth = badgeTextWidth + badgePaddingX * 2;
      const badgeX = pageWidth - margin - badgeWidth;
      const badgeY = y - badgeHeight + 5;
      doc.setFillColor(rgbBg[0], rgbBg[1], rgbBg[2]);
      doc.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, badgeHeight / 2, badgeHeight / 2, "F");
      doc.setTextColor(rgb[0], rgb[1], rgb[2]);
      doc.text(badgeText, badgeX + badgePaddingX, badgeY + badgeHeight / 2 + 3.2);

      y += 28;

      // ---- Card drawing helpers
      const rowLineHeight = 13;
      const rowGap = 9;
      const cardPadding = 16;
      const sectionGap = 16;

      const measureRows = (rows: DetailRow[], innerWidth: number) => {
        let h = 0;
        rows.forEach((row) => {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9.5);
          const wrapped = doc.splitTextToSize(row.value, innerWidth * 0.55);
          h += Math.max(1, wrapped.length) * rowLineHeight + rowGap;
        });
        return h;
      };

      const drawRows = (rows: DetailRow[], x: number, innerWidth: number, startY: number) => {
        let ry = startY;
        rows.forEach((row) => {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9.5);
          doc.setTextColor(107, 114, 128);
          doc.text(row.label, x, ry);

          doc.setFont("helvetica", "bold");
          doc.setFontSize(9.5);
          doc.setTextColor(17, 24, 39);
          const wrapped: string[] = doc.splitTextToSize(row.value, innerWidth * 0.55);
          wrapped.forEach((line, i) => {
            const lineWidth = doc.getTextWidth(line);
            doc.text(line, x + innerWidth - lineWidth, ry + i * rowLineHeight);
          });

          ry += Math.max(1, wrapped.length) * rowLineHeight + rowGap;
        });
        return ry;
      };

      const drawCard = (title: string | null, rows: DetailRow[]) => {
        if (!rows.length) return;
        const innerWidth = contentWidth - cardPadding * 2;
        const titleHeight = title ? 22 : 0;
        const rowsHeight = measureRows(rows, innerWidth);
        const cardHeight = cardPadding * 2 + titleHeight + rowsHeight - rowGap + 4;

        if (y + cardHeight > pageHeight - 90) {
          doc.addPage();
          y = margin;
        }

        doc.setFillColor(249, 250, 251);
        doc.setDrawColor(229, 231, 235);
        doc.roundedRect(margin, y, contentWidth, cardHeight, 8, 8, "FD");

        let innerY = y + cardPadding + 4;
        if (title) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10.5);
          doc.setTextColor(55, 65, 81);
          doc.text(title, margin + cardPadding, innerY);
          innerY += titleHeight;
        }

        drawRows(rows, margin + cardPadding, innerWidth, innerY);
        y += cardHeight + sectionGap;
      };

      // ---- Sections
      drawCard(null, getMainRows());
      drawCard("Beneficiary Details", getPaymentDetailRows(order.buyerCreditPaymentDetails));
      drawCard("Paid From", getPaymentDetailRows(order.buyerDebitPaymentDetails));
      drawCard("Payment Collection Details", getPaymentDetailRows(order.sellerCreditPaymentDetails));

      // ---- Footer
      if (y > pageHeight - 70) {
        doc.addPage();
        y = margin;
      }
      doc.setDrawColor(229, 231, 235);
      doc.line(margin, y, pageWidth - margin, y);
      y += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(`Generated on ${new Date().toLocaleString("en-US")}`, margin, y);
      y += 11;
      doc.text("This is a system-generated receipt.", margin, y);

      doc.save(receiptFilename("pdf"));
    } finally {
      setDownloading(false);
    }
  };

  const wrapCanvasText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
  ): string[] => {
    const words = text.split(" ");
    const lines: string[] = [];
    let current = "";
    words.forEach((word) => {
      const test = current ? `${current} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    });
    if (current) lines.push(current);
    return lines.length ? lines : [""];
  };

  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  };

  const handleDownloadPNG = async () => {
    setDownloading(true);
    try {
      // const flags = await loadFlagCanvases();

      const width = 640;
      const padding = 40;
      const contentWidth = width - padding * 2;
      const rowLineHeight = 18;
      const rowGap = 8;
      const cardPadding = 18;
      const sectionGap = 18;

      const measureCanvas = document.createElement("canvas");
      const mctx = measureCanvas.getContext("2d")!;

      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      const logoLoaded = new Promise<boolean>((resolve) => {
        logoImg.onload = () => resolve(true);
        logoImg.onerror = () => resolve(false);
        logoImg.src = "/images/logo/SecondaryLogoPNG.png";
      });
      const hasLogo = await logoLoaded;

      const measureRows = (rows: DetailRow[], innerWidth: number) => {
        let h = 0;
        rows.forEach((row) => {
          mctx.font = "600 13px Arial";
          const wrapped = wrapCanvasText(mctx, row.value, innerWidth * 0.55);
          h += Math.max(1, wrapped.length) * rowLineHeight + rowGap;
        });
        return h;
      };

      const measureCardHeight = (title: string | null, rows: DetailRow[]) => {
        if (!rows.length) return 0;
        const innerWidth = contentWidth - cardPadding * 2;
        const titleHeight = title ? 26 : 0;
        return cardPadding * 2 + titleHeight + measureRows(rows, innerWidth) - rowGap + 4;
      };

      const mainRows = getMainRows();
      const beneficiaryRows = getPaymentDetailRows(order.buyerCreditPaymentDetails);
      const debitRows = getPaymentDetailRows(order.buyerDebitPaymentDetails);
      const sellerRows = getPaymentDetailRows(order.sellerCreditPaymentDetails);

      let totalHeight = 0;
      // totalHeight += 72; // brand header band
      // totalHeight += padding; // top padding under header
      totalHeight += 90; // logo header block
      // totalHeight += 34 + 26; // flags row
      totalHeight += 26;
      totalHeight += 40; // amount row
      totalHeight += measureCardHeight(null, mainRows) + sectionGap;
      totalHeight += measureCardHeight("Beneficiary Details", beneficiaryRows) + (beneficiaryRows.length ? sectionGap : 0);
      totalHeight += measureCardHeight("Paid From", debitRows) + (debitRows.length ? sectionGap : 0);
      totalHeight += measureCardHeight("Payment Collection Details", sellerRows) + (sellerRows.length ? sectionGap : 0);
      totalHeight += 60; // footer
      totalHeight += padding;

      const scale = 2;
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = totalHeight * scale;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${totalHeight}px`;
      const ctx = canvas.getContext("2d")!;
      ctx.scale(scale, scale);

      // background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, totalHeight);

      let y = padding;
      ctx.textBaseline = "top";

      if (hasLogo) {
        const logoMaxWidth = 120;
        const logoMaxHeight = 56;
        const ratio = Math.min(logoMaxWidth / logoImg.naturalWidth, logoMaxHeight / logoImg.naturalHeight);
        const logoW = logoImg.naturalWidth * ratio;
        const logoH = logoImg.naturalHeight * ratio;
        ctx.drawImage(logoImg, width - padding - logoW, y, logoW, logoH);
      }

      ctx.fillStyle = "#111827";
      ctx.font = "700 20px Arial";
      ctx.fillText("Order Receipt", padding, y);
      y += 28;

      ctx.fillStyle = "#6b7280";
      ctx.font = "400 12px Arial";
      ctx.fillText(
        `${order.fromCurrency.abbr}/${order.toCurrency.abbr} | ${formatDate(order.createdAt)}`,
        padding,
        y
      );
      y += 40;

      // amount + status badge
      ctx.fillStyle = "#111827";
      ctx.font = "700 28px Arial";
      ctx.fillText(`${order.fromCurrency.abbr}${order.amount.toLocaleString()}`, padding, y);

      const { hex, hexBg } = (() => {
        const map: Record<string, { hex: string; hexBg: string }> = {
          SUCCESSFUL: { hex: "#16a34a", hexBg: "#dcfce7" },
          PENDING: { hex: "#ca8a04", hexBg: "#fef9c3" },
          PROCESSING: { hex: "#ca8a04", hexBg: "#fef9c3" },
          FAILED: { hex: "#dc2626", hexBg: "#fee2e2" },
        };
        return map[order.status] || { hex: "#4b5563", hexBg: "#f3f4f6" };
      })();

      ctx.font = "700 12px Arial";
      const badgeText = order.status;
      const badgeTextWidth = ctx.measureText(badgeText).width;
      const badgePaddingX = 10;
      const badgeHeight = 24;
      const badgeWidth = badgeTextWidth + badgePaddingX * 2;
      const badgeX = width - padding - badgeWidth;
      const badgeY = y - badgeHeight + 6;
      ctx.fillStyle = hexBg;
      drawRoundedRect(ctx, badgeX, badgeY, badgeWidth, badgeHeight, badgeHeight / 2);
      ctx.fill();
      ctx.fillStyle = hex;
      ctx.fillText(badgeText, badgeX + badgePaddingX, badgeY + badgeHeight / 2 + 4);

      y += 34;

      const drawRows = (rows: DetailRow[], x: number, innerWidth: number, startY: number) => {
        let ry = startY;
        rows.forEach((row) => {
          ctx.fillStyle = "#6b7280";
          ctx.font = "600 12px Arial";
          ctx.fillText(row.label, x, ry);

          ctx.fillStyle = "#111827";
          ctx.font = "700 12px Arial";
          const wrapped = wrapCanvasText(ctx, row.value, innerWidth * 0.55);
          wrapped.forEach((line, i) => {
            const lineWidth = ctx.measureText(line).width;
            ctx.fillText(line, x + innerWidth - lineWidth, ry + i * rowLineHeight);
          });

          ry += Math.max(1, wrapped.length) * rowLineHeight + rowGap;
        });
        return ry;
      };

      const drawCard = (title: string | null, rows: DetailRow[]) => {
        if (!rows.length) return;
        const innerWidth = contentWidth - cardPadding * 2;
        const cardHeight = measureCardHeight(title, rows);

        ctx.fillStyle = "#f9fafb";
        drawRoundedRect(ctx, padding, y, contentWidth, cardHeight, 10);
        ctx.fill();
        ctx.strokeStyle = "#e5e7eb";
        ctx.lineWidth = 1;
        drawRoundedRect(ctx, padding, y, contentWidth, cardHeight, 10);
        ctx.stroke();

        let innerY = y + cardPadding + 6;
        if (title) {
          ctx.fillStyle = "#374151";
          ctx.font = "700 13px Arial";
          ctx.fillText(title, padding + cardPadding, innerY);
          innerY += 26;
        }

        drawRows(rows, padding + cardPadding, innerWidth, innerY);
        y += cardHeight + sectionGap;
      };

      drawCard(null, mainRows);
      drawCard("Beneficiary Details", beneficiaryRows);
      drawCard("Paid From", debitRows);
      drawCard("Payment Collection Details", sellerRows);

      // footer
      ctx.strokeStyle = "#e5e7eb";
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
      y += 24;
      ctx.fillStyle = "#9ca3af";
      ctx.font = "400 10px Arial";
      ctx.fillText(`Generated on ${new Date().toLocaleString("en-US")}`, padding, y);
      y += 15;
      ctx.fillText("This is a system-generated receipt.", padding, y);

      const link = document.createElement("a");
      link.download = receiptFilename("png");
      link.href = canvas.toDataURL("image/png");
      link.click();
    } finally {
      setDownloading(false);
    }
  };

  const renderSellerPaymentDetails = () => {
    if (!order?.sellerCreditPaymentDetails) return null;

    return (
      <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
        <div className="space-y-3">
          {/* Amount */}
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-300">Amount</span>
            <span className="text-gray-800 dark:text-white">
              {order.fromCurrency.symbol}
              {order.amount.toLocaleString()}
            </span>
          </div>

          {/* BANK_TRANSFER */}
            {(order.sellerCreditPaymentDetails.paymentMethod === PAYMENT_METHOD.BANK_TRANSFER ||
  order.sellerCreditPaymentDetails.paymentMethod === PAYMENT_METHOD.ACH ||
  order.sellerCreditPaymentDetails.paymentMethod === PAYMENT_METHOD.WIRE) && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">
                  Account Name
                </span>
                <span className="text-gray-800 dark:text-white">
                  {order.sellerCreditPaymentDetails.accountName}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">
                  Account Number
                </span>
                <span className="text-gray-800 dark:text-white">
                  {order.sellerCreditPaymentDetails.accountNumber}
                </span>
              </div>

              {order.sellerCreditPaymentDetails.currency?.bankCodeLabel && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    {order.sellerCreditPaymentDetails.currency.bankCodeLabel}
                  </span>
                  <span className="text-gray-800 dark:text-white">
                    {order.sellerCreditPaymentDetails.bankCode}
                  </span>
                </div>
              )}
              {((order.fromCurrency.abbr === "NGN" || order.fromCurrency.abbr === "CNY" || order.fromCurrency.abbr === "ZAR") ||
                MOMO_COUNTRIES_ISO2.has(
                  order.sellerCreditPaymentDetails.iso2,
                )) &&
                order.sellerCreditPaymentDetails.providerName && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">
                      Bank Name
                    </span>
                    <span className="text-gray-800 dark:text-white">
                      {order.sellerCreditPaymentDetails.providerName}
                    </span>
                  </div>
                )}

{(order.fromCurrency.abbr === "GBP" || order.fromCurrency.abbr === "EUR") &&
  order.sellerCreditPaymentDetails.providerName &&
  order.sellerCreditPaymentDetails.providerName !== order.sellerCreditPaymentDetails.bankCode && (
    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-300">Bank Name</span>
      <span className="text-gray-800 dark:text-white">
        {order.sellerCreditPaymentDetails.providerName}
      </span>
    </div>
  )}
                {(order.sellerCreditPaymentDetails.holderCity ||
  order.sellerCreditPaymentDetails.holderStreet ||
  order.sellerCreditPaymentDetails.holderPostalCode) && (
  <div className="flex justify-between">
    <span className="text-gray-600 dark:text-gray-300">
      Holder's Address
    </span>
    <span className="text-gray-800 dark:text-white text-right">
      {[
        order.sellerCreditPaymentDetails.holderStreet,
        order.sellerCreditPaymentDetails.holderCity,
        order.sellerCreditPaymentDetails.holderPostalCode,
      ]
        .filter(Boolean)
        .join(", ")}
    </span>
  </div>
)}
            </>
          )}

          {/* WECHAT_PAY / ALIPAY */}
{(order.sellerCreditPaymentDetails.paymentMethod === PAYMENT_METHOD.WECHAT_PAY ||
  order.sellerCreditPaymentDetails.paymentMethod === PAYMENT_METHOD.ALIPAY) && (
  <>
    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-300">Account Name</span>
      <span className="text-gray-800 dark:text-white">
        {order.sellerCreditPaymentDetails.accountName}
      </span>
    </div>
    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-300">Phone Number</span>
      <span className="text-gray-800 dark:text-white">
        {order.sellerCreditPaymentDetails.phoneNumber}
      </span>
    </div>
  </>
)}

          {/* MOBILE_MONEY */}
          {order.sellerCreditPaymentDetails.paymentMethod ===
            PAYMENT_METHOD.MOBILE_MONEY && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">
                  {order.sellerCreditPaymentDetails.isUssdCode
                    ? "Account Name"
                    : "MoMo Name"}
                </span>
                <span className="text-gray-800 dark:text-white">
                  {order.sellerCreditPaymentDetails.accountName}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">
                  {order.sellerCreditPaymentDetails.isUssdCode
                    ? "USSD Code"
                    : "MoMo Number"}
                </span>
                <span className="text-gray-800 dark:text-white">
                  {/* {order.sellerCreditPaymentDetails.accountNumber} */}
                  {order.sellerCreditPaymentDetails.isUssdCode
                    ? `${order.sellerCreditPaymentDetails.accountNumber}${order.amount}#`
                    : order.sellerCreditPaymentDetails.accountNumber}
                </span>
              </div>

              {order.sellerCreditPaymentDetails.providerName && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">
                    Network Carrier
                  </span>
                  <span className="text-gray-800 dark:text-white">
                    {order.sellerCreditPaymentDetails.providerName}
                  </span>
                </div>
              )}
            </>
          )}

          {/* STABLE_CURRENCY */}
          {order.sellerCreditPaymentDetails.paymentMethod ===
            PAYMENT_METHOD.STABLE_CURRENCY && (
            <>
              <div className="space-y-1">
                {/* Label */}
                <span className="block text-gray-600 dark:text-gray-300">
                  Wallet Address:
                </span>

                {/* Address + Copy */}
                <div className="flex items-start gap-2 max-w-full">
                  <p className="text-gray-800 dark:text-white break-all whitespace-normal flex-1 max-w-full">
                    {order.sellerCreditPaymentDetails.walletAddress}
                  </p>
                  <CopyButton
                    text={order.sellerCreditPaymentDetails.walletAddress!}
                  />
                </div>
              </div>

              <div className="flex justify-between mt-3">
                <span className="text-gray-600 dark:text-gray-300">
                  Network:
                </span>
                <span className="text-gray-800 dark:text-white">
                  {renderblockchainName()}
                </span>
              </div>
            </>
          )}

          {order.sellerCreditPaymentDetails.paymentMethod &&
            EMAIL_PAYMENT_METHODS.has(
              order.sellerCreditPaymentDetails.paymentMethod,
            ) && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    Account Name
                  </span>
                  <span className="text-gray-800 dark:text-white">
                    {order.sellerCreditPaymentDetails.accountName}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    Email
                  </span>
                  <span className="text-gray-800 dark:text-white">
                    {order.sellerCreditPaymentDetails.email}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    Payment Method
                  </span>
                  <span className="text-gray-800 dark:text-white">
                    {order.sellerCreditPaymentDetails.paymentMethod}
                  </span>
                </div>
              </>
            )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white/90 mb-6">
            Order Details
          </h3>
          <div className="space-y-6">
            {/* Currency Pair Section */}
            <div className="flex items-start gap-4">
              <div className="flex items-center -space-x-2">
                <div className="h-16 w-16 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                  <img
                    src={order.fromCurrency.flag}
                    className="h-full w-full object-cover"
                    alt={order.fromCurrency.name}
                  />
                </div>
                <div className="h-16 w-16 rounded-full overflow-hidden border-2 border-white dark:border-gray-800">
                  <img
                    src={order.toCurrency.flag}
                    className="h-full w-full object-cover"
                    alt={order.toCurrency.name}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Currency Pair
                </h4>
                <p className="font-medium text-gray-800 dark:text-white/90">
                  {order.fromCurrency.abbr}/{order.toCurrency.abbr}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {order.fromCurrency.name} to {order.toCurrency.name}
                </p>
              </div>
            </div>

            {/* Details Section */}
            <div className="space-y-4">
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Transaction Details
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Amount to pay
                    </span>
                    <span className="text-gray-800 dark:text-white/90">
                      {order.fromCurrency.symbol}
                      {order.amount.toLocaleString()}
                    </span>
                  </div>
                  {order.fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Amount
                      </span>
                      <span className="text-gray-800 dark:text-white/90">
                        {order.toCurrency.symbol}
                        {order.value.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {order.fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Fee
                      </span>
                      <span className="text-gray-800 dark:text-white/90">
                        {order.toCurrency.symbol}
                        {order.fee.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Amount to receive
                    </span>
                    <span className="text-gray-800 dark:text-white/90">
                      {showValue(order)}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Rate
                    </span>
                    <span className="text-gray-800 dark:text-white/90">
                      {formatRateDisplay()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Date
                    </span>
                    <span className="text-gray-800 dark:text-white/90">
                      {formatDate(order.createdAt)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Reference
                    </span>
                    <span className="text-gray-800 dark:text-white/90">
                      {order.paymentReferenceCode || "N/A"}
                    </span>
                  </div>
                  {order.narration && (
  <div className="flex justify-between">
    <span className="text-gray-500 dark:text-gray-400">Narration</span>
    <span className="text-gray-800 dark:text-white/90">{order.narration}</span>
  </div>
)}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 dark:text-gray-400">
                      Status
                    </span>
                    <Badge
                      size="sm"
                      color={getOrderStatusBadgeColor(order.status)}
                    >
                      {order.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Payment Details Section */}
              {order.buyerCreditPaymentDetails && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Beneficiaries Details
                  </h4>

                  {/* EMAIL PAYMENT METHOD DETAILS */}
                  {order.buyerCreditPaymentDetails.paymentMethod ===
                  PAYMENT_METHOD.STABLE_CURRENCY ? (
                    <div className="space-y-3">
                      <div className="flex flex-row justify-between">
                        <span className="block text-gray-500 dark:text-gray-400">
                          Wallet Address:
                        </span>
                        <div className="flex items-start gap-1">
                          <p className="text-gray-800 dark:text-white/90 whitespace-normal">
                            {order.buyerCreditPaymentDetails.walletAddress}
                          </p>
                          <CopyButton
                            text={
                              order.buyerCreditPaymentDetails.walletAddress!
                            }
                          />
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Network:
                        </span>
                        <span className="text-gray-800 dark:text-white/90">
                          {getBlockchainName(
                            order.buyerCreditPaymentDetails
                              .network as unknown as BLOCKCHAIN_NETWORK,
                          )}
                        </span>
                      </div>
                    </div>
                  ) : // ) : EMAIL_PAYMENT_METHODS.has(order.buyerCreditPaymentDetails.paymentMethod) ? (
                  order.buyerCreditPaymentDetails.paymentMethod ===
                      PAYMENT_METHOD.WECHAT_PAY ||
                    order.buyerCreditPaymentDetails.paymentMethod ===
                      PAYMENT_METHOD.ALIPAY ? (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Account Name
                        </span>
                        <span className="text-gray-800 dark:text-white/90">
                          {order.buyerCreditPaymentDetails.accountName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Phone Number
                        </span>
                        <span className="text-gray-800 dark:text-white/90">
                          {order.buyerCreditPaymentDetails.phoneNumber}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Payment Method
                        </span>
                        <span className="text-gray-800 dark:text-white/90">
                          {order.buyerCreditPaymentDetails.paymentMethod}
                        </span>
                      </div>
                    </div>
                  ) : EMAIL_PAYMENT_METHODS.has(
                      order.buyerCreditPaymentDetails.paymentMethod,
                    ) ? (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Account Name
                        </span>
                        <span className="text-gray-800 dark:text-white/90">
                          {order.buyerCreditPaymentDetails.accountName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Email
                        </span>
                        <span className="text-gray-800 dark:text-white/90">
                          {order.buyerCreditPaymentDetails.email}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Payment Method
                        </span>
                        <span className="text-gray-800 dark:text-white/90">
                          {order.buyerCreditPaymentDetails.paymentMethod}
                        </span>
                      </div>
                    </div>
                  ) : (
                    // ✅ For all other methods (Wallet, Bank Transfer, Mobile Money, …)
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Account Name
                        </span>
                        <span className="text-gray-800 dark:text-white/90">
                          {order.buyerCreditPaymentDetails.accountName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          {order.buyerCreditPaymentDetails.paymentMethod ===
                          "MOBILE_MONEY"
                            ? "MoMo Number"
                            : "Account Number"}
                        </span>
                        <span className="text-gray-800 dark:text-white/90">
                          {order.buyerCreditPaymentDetails.accountNumber}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          {order.buyerCreditPaymentDetails?.currency
                            .bankCodeLabel === "Routing No."
                            ? "Routing No."
                            : order.buyerCreditPaymentDetails?.currency
                                  .bankCodeLabel === "Sort code"
                              ? "Sort code"
                              : order.buyerCreditPaymentDetails?.currency
                                    .bankCodeLabel === "IBAN"
                                ? "IBAN"
                                : order.buyerCreditPaymentDetails
                                      ?.paymentMethod === "MOBILE_MONEY"
                                  ? "Network Carrier"
                                  : "Bank Name"}
                        </span>
                        <span className="text-gray-800 dark:text-white/90">
                          {order.buyerCreditPaymentDetails.providerName}
                        </span>
                      </div>
                      {(order.buyerCreditPaymentDetails.holderCity ||
  order.buyerCreditPaymentDetails.holderStreet ||
  order.buyerCreditPaymentDetails.holderPostalCode) && (
  <div className="flex justify-between">
    <span className="text-gray-500 dark:text-gray-400">
      Holder's Address
    </span>
    <span className="text-gray-800 dark:text-white/90 text-right">
      {[
        order.buyerCreditPaymentDetails.holderStreet,
        order.buyerCreditPaymentDetails.holderCity,
        order.buyerCreditPaymentDetails.holderPostalCode,
      ]
        .filter(Boolean)
        .join(", ")}
    </span>
  </div>
)}
                    </div>
                    
                  )}
                </div>
              )}

              {buyerDebitPaymentMethod !== PAYMENT_METHOD.WALLET && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => setSellerModalOpen(true)}
                >
                  View Payment Details
                </Button>
              )}
            </div>

            {/* Close / Download Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="relative" ref={downloadMenuRef}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDownloadMenuOpen((o) => !o)}
                  disabled={downloading}
                >
                  <span className="flex items-center gap-1.5">
                    {downloading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    {downloading ? "Generating..." : "Download Receipt"}
                    {!downloading && <ChevronDown className="h-3.5 w-3.5" />}
                  </span>
                </Button>

                {downloadMenuOpen && (
                  <div className="absolute right-0 bottom-full mb-2 w-44 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 z-10">
                    <button
                      onClick={() => {
                        setDownloadMenuOpen(false);
                        handleDownloadPDF();
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700 rounded-t-lg"
                    >
                      <FileText className="h-4 w-4" />
                      Download as PDF
                    </button>
                    <button
                      onClick={() => {
                        setDownloadMenuOpen(false);
                        handleDownloadPNG();
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700 rounded-b-lg"
                    >
                      <ImageIcon className="h-4 w-4" />
                      Download as Image
                    </button>
                  </div>
                )}
              </div>
              <Button size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Second Modal for Seller Payment Details */}
      <Modal
        isOpen={isSellerModalOpen}
        onClose={paymentClose}
        className="max-w-md"
      >
        <div className="p-6">
          {error && (
            <div className="mb-4">
              <Alert
                variant="error"
                title="Error"
                message={error}
                showLink={false}
              />
            </div>
          )}

          {success && (
            <div className="mb-4">
              <Alert
                variant="success"
                title="Success"
                message={success}
                showLink={false}
              />
            </div>
          )}
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white/90 mb-4">
            Payment Details
          </h3>

          {order?.sellerCreditPaymentDetails?.isUssdCode ? (
            <div className="mb-6 flex items-start gap-3 rounded-lg bg-blue-50 p-4 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
              <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p>
                The USSD code below belongs to Monirates and/or its Partner. Your Payment is safe and secure. Please dial the code to complete your
                payment and upload proof of payment.
              </p>
            </div>
          ) : order.sellerCreditPaymentDetails?.paymentMethod ===
            PAYMENT_METHOD.STABLE_CURRENCY ? (
            <div className="mb-6 flex items-start gap-3 rounded-lg bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
              <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p>
                The wallet address below belongs to Monirates and/or its Partner. Your Payment is safe and secure. Please transfer the amount to the account.
              </p>
            </div>
          ) : (
            <div className="mb-6 flex items-start gap-3 rounded-lg bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
              <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p>
                The account details below belongs to Monirates and/or its Partner. Your Payment is safe and secure. Please transfer the amount to the account.
              </p>
            </div>
          )}

          {renderSellerPaymentDetails()}

          {/* File upload section - only for MOBILE_MONEY */}
          {order.sellerCreditPaymentDetails?.paymentMethod === "MOBILE_MONEY" &&
            !order.buyerMadePayment && (
              <div className="mt-6">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  Please upload proof of payment
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*,.pdf"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 dark:border-gray-600"
                >
                  {paymentProof ? paymentProof.name : "Select File"}
                </button>
                {paymentProof && (
                  <p className="mt-1 text-sm text-green-600">File selected</p>
                )}
              </div>
            )}

          {/* Payment confirmation button - always visible */}
          <Button
            size="sm"
            className="mt-6 w-full"
            onClick={handleProofSubmit}
            disabled={
              order.status === "SUCCESSFUL" ||
              order.buyerMadePayment ||
              buyerMadePayment ||
              isSubmitting ||
              (order.sellerCreditPaymentDetails?.paymentMethod ===
                "MOBILE_MONEY" &&
                !paymentProof)
            }
          >
            {/* {isSubmitting ? "Processing..." : "I have made payment"} */}
            {order.status === "SUCCESSFUL"
              ? "Transaction Completed"
              : buyerMadePayment
                ? "Money already sent"
                : isSubmitting
                  ? "Processing..."
                  : "I have sent the money"}
          </Button>

          <div className="flex justify-end mt-4">
            <Button size="sm" variant="outline" onClick={paymentClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}